// Client-side wrapper around the backend's per-order revision tracking.
//
// Backend contract (Base44 → Cabinet Depot handoff v1.0, 2026-06):
//   Order-level:
//     • revision_number: int (0 for un-modified, bumps by 1 each modification)
//     • last_seen_revision_number: int (server-side per-order ack — shared across all users)
//     • modification_log: newline-delimited string, oldest-first (we display newest-first)
//     • last_revised_at: ISO-8601 timestamp of most recent modification
//     • updated_date: secondary freshness signal
//   Item-level:
//     • added_at_revision: int → green "NEW · REV N" ribbon
//     • removed_at_revision: int → red "REMOVED UPSTREAM · REV N" pill (item stays for audit)
//     • removed_at: ISO timestamp when the removal was detected
//     • qty_changed_at_revision: int, qty_previous: int, qty_new: int → yellow "QTY X→Y · REV N"
//     • qty_changed_at: ISO timestamp
//
// Ack endpoint: POST /api/orders/{id}/acknowledge-revision → bumps last_seen_revision_number
//   to the current revision_number server-side.
//
// We fall back to localStorage (per-browser) when the server-side field is absent (offline,
// or an order that predates the field). Server value always takes precedence when present.

import api from "@/lib/apiClient";

const STORAGE_PREFIX = "cd_order_seen_rev_";

export function normalizeRevision(rev) {
  if (rev == null) return null;
  const n = typeof rev === "number" ? rev : parseInt(rev, 10);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/** Local (per-browser) fallback for orders where the server-side field is null. */
function getLocalSeenRevision(orderId) {
  if (!orderId) return null;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + orderId);
    return normalizeRevision(raw);
  } catch (err) {
    return null;
  }
}
function setLocalSeenRevision(orderId, rev) {
  if (!orderId) return;
  const n = normalizeRevision(rev);
  if (n == null) return;
  try { localStorage.setItem(STORAGE_PREFIX + orderId, String(n)); }
  catch (err) { /* noop */ }
}

/**
 * Best available "last seen revision" for the order — prefers the server-side
 * shared ack (last_seen_revision_number) and falls back to per-browser localStorage.
 */
export function getSeenRevision(order) {
  if (!order) return null;
  const server = normalizeRevision(order.last_seen_revision_number);
  if (server != null) return server;
  return getLocalSeenRevision(order.id);
}

/** True when revision_number > 0 (order has been modified upstream at least once). */
export function isOrderModified(order) {
  if (!order) return false;
  const rev = normalizeRevision(order.revision_number);
  return rev != null && rev > 0;
}

/** True when the order has a new revision the current viewer hasn't acked yet. */
export function hasUnseenModification(order) {
  if (!isOrderModified(order)) return false;
  const current = normalizeRevision(order.revision_number);
  const seen = getSeenRevision(order);
  if (seen == null) return true;
  return current > seen;
}

/** Split modification_log into newest-first display lines. */
export function parseModificationLog(log) {
  if (!log || typeof log !== "string") return [];
  return log
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .reverse();
}

/** Extract the "Rev N" number from a log line, or null if the prefix is missing. */
export function parseLogLineRev(line) {
  if (!line || typeof line !== "string") return null;
  const m = line.match(/^\s*Rev(?:ision)?\s*#?\s*(\d+)\s*[:.\-–—]?/i);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Diff summary derived from item-level revision stamps. Returns counts for the
 * `+N ADDED · −N REMOVED · N QTY CHANGE` chip strip. Only counts items whose
 * stamp revision is greater than the seen revision — so once the user acks,
 * the counts drop back to 0 without needing a re-fetch.
 */
export function itemRevisionSummary(order) {
  const items = (order && order.items) || [];
  const seen = getSeenRevision(order) ?? 0;
  let added = 0, removed = 0, qtyChanged = 0;
  for (const it of items) {
    const a = normalizeRevision(it.added_at_revision);
    const r = normalizeRevision(it.removed_at_revision);
    const q = normalizeRevision(it.qty_changed_at_revision);
    if (a != null && a > seen) added += 1;
    if (r != null && r > seen) removed += 1;
    if (q != null && q > seen) qtyChanged += 1;
  }
  return { added, removed, qtyChanged };
}

/** Per-item helper — which ribbon (if any) should render for this item. */
export function itemRevisionState(item, order) {
  if (!item) return null;
  const seen = getSeenRevision(order) ?? 0;
  const a = normalizeRevision(item.added_at_revision);
  const r = normalizeRevision(item.removed_at_revision);
  const q = normalizeRevision(item.qty_changed_at_revision);
  if (r != null && r > 0) {
    return { kind: "removed", rev: r, unseen: r > seen };
  }
  if (a != null && a > 0) {
    return { kind: "added", rev: a, unseen: a > seen };
  }
  if (q != null && q > 0) {
    return {
      kind: "qty",
      rev: q,
      unseen: q > seen,
      prev: normalizeRevision(item.qty_previous),
      next: normalizeRevision(item.qty_new),
    };
  }
  return null;
}

/**
 * Acknowledge the order's current revision. Bumps server-side
 * last_seen_revision_number then mirrors to localStorage as a fallback for
 * environments where the endpoint might briefly fail. Returns true on success.
 */
export async function acknowledgeRevision(order) {
  if (!order || !order.id) return false;
  const rev = normalizeRevision(order.revision_number);
  if (rev == null) return false;
  let serverOk = false;
  try {
    await api.post(`/orders/${order.id}/acknowledge-revision`);
    serverOk = true;
  } catch (err) {
    // Server-side ack failed (offline, endpoint not deployed, etc.) —
    // silently fall back to localStorage so the UI doesn't stall.
    console.warn("acknowledgeRevision: server call failed, using localStorage fallback", err);
  }
  setLocalSeenRevision(order.id, rev);
  return serverOk;
}
